import { Suspense, lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { RouteObject } from 'react-router';
import SidebarLayout from 'src/layouts/SidebarLayout';
import BaseLayout from 'src/layouts/BaseLayout';
import SuspenseLoader from 'src/components/SuspenseLoader';
import ProtectedRoutes from './common/ProtectedRoutes';
import { RequirePermission } from './permissions';
import Forgot from './content/forgot/components/Forgot';
import ResetPassword from './content/forgot/components/PasswordReset';
import SquareSpaceAuthSignUp from 'src/content/signup/components/SquareSpaceAuthSignUp';
import QueryParamHandler from './common/QueryParameterHandler';
import RenderLeadForm from './content/leadGeneration/RenderLeadForm';
import SquareSpaceAuthConnect from './content/squareSpaceConnect/SquareSpaceConnect';
import ChangePlanNew from './content/billing/components/ChangePlanNew';
import { DisconnectGSCProvider } from './contexts/disconnectGSCContext';
import BackLinkPage from './content/OtherTools/BackLinkPage';
import { RecommendActionsCard } from './content/localSeo/RecommendActionsCard';
import { ManageReviewsDashboard } from './content/localSeo/ManageReviewsDashboard';
import { Posting } from './content/localSeo/Posting';
import { ListingManagerDashboard } from './content/localSeo/ListingManagerDashboard';
import { WidgetPreviewComponent } from './content/localSeo/EmbedGoogleReviewsComponents/WidgetPreviewComponent';
import Loader from './common/config/Loader';
import { CitationRoot } from './content/CitationBuilderV2';
import { CampaignsList } from './content/CitationBuilderV2/components';
import { CreateCampaign } from './content/CitationBuilderV2/components/CreateCampaign';
import { ConfirmAndPay } from './content/CitationBuilderV2/components/confirmAndPay';
import { BusinessProfileAutocompleteProvider } from './components/BusinessProfileAutocomplete';
import { CampaignInProgress } from './content/CitationBuilderV2/components/CampaignInProgress';
import { CitationReport } from './content/CitationBuilderV2/components/Report';
import { CitationBuilder } from './content/CitationBuilderV2/components/CitationBuilder';
import { GoogleBusinessProfileProvider } from './components/GoogleBusinessProfileSelector';
import { CitationProvider } from './content/CitationBuilderV2/Providers/CitationProvider';
import { CitationScanning } from './content/CitationBuilderV2/components/CitationScanning';
import { ComingSoonCitationBuilder } from './content/CitationBuilderV2/components/ComingSoonCitationBuilder';
import { RouteDataLoader } from './common/RouteDataLoader';
import { decryptEncryptedJWT, getCookie } from './common/config/FunctionHelper';
import PDFBuilderPublicRoute from './content/pdfBuilder/PDFBuilderPublicRoute';
import ProtectedRouteV2 from './common/ProtectedRouteV2';
import { ErrorBoundaryV2 } from './common/ErrorBoundaryV2';
import KeywordAssistPage from './content/orbitAi/keyword-assist';
import ContentStrategyPage from './content/orbitAi/content-strategy';
// Todo: Content Plan - Import ContentPlanPage component once Content Plan feature is enabled
// import ContentPlanPage from './content/orbitAi/content-plan';
import GetAffiliateCode from './content/signup/components/GetAffiliateCode';

import BlogGenerationPage from './content/orbitAi/blog-generation';
import LocalSeoAiPage from './content/orbitAi/local-seo';
import ReviewAutomationPage from './content/orbitAi/review-automation';
import OrbitAIPublicLinkHandler from './content/orbitAi/Components/OrbitAIPublicLinkHandler';

const OrbitAiDashboard = Loader(lazy(() => import('src/content/orbitAi')));

//const Loader = (Component) => (props) =>
//  (
//    <Suspense fallback={<SuspenseLoader />}>
//      <Component {...props} />
//    </Suspense>
//  );

//Message
const MessagePage = Loader(
  lazy(() => import('src/content/stripeMessage/components/MessagePage'))
);

// Pages
const Overview = Loader(lazy(() => import('src/content/overview')));

// Applications
const UserSettings = Loader(
  lazy(() => import('src/content/applications/Users/settings'))
);

// Status
const Status404 = Loader(
  lazy(() => import('src/content/pages/Status/Status404'))
);

const Status500 = Loader(
  lazy(() => import('src/content/pages/Status/Status500'))
);

const StatusMaintenance = Loader(
  lazy(() => import('src/content/pages/Status/StatusMaintenance'))
);

// SignUp
const Signup = Loader(lazy(() => import('src/content/signup/components')));


// Site Audit
const SiteAudit = Loader(lazy(() => import('src/content/siteAudit')));

// Individual Scans
const IndividualScans = Loader(
  lazy(() => import('src/content/individualScans'))
);

// GetMoreScan
const GetMoreScan = Loader(
  lazy(() => import('src/content/billing/components'))
);

// Price Plan
const PricePlan = Loader(lazy(() => import('src/content/stripe')));

const PlanNew = Loader(lazy(() => import('src/content/stripe')));

// Addon Buy status
const AddOnStatus = Loader(
  lazy(() => import('src/content/stripeMessage/components/AddonMessagePage'))
);

const GoogleAuth = Loader(lazy(() => import('src/content/Google/Auth')));

//User Management
const UserManagement = Loader(lazy(() => import('src/content/userManagement')));

//Member
const Member = Loader(lazy(() => import('src/content/member')));

// LeadGeneration
const LeadGeneration = Loader(lazy(() => import('src/content/leadGeneration')));

const OnBoarding = Loader(lazy(() => import('src/content/onBoarding')));

const OnBoardingAutoStart = Loader(
  lazy(() => import('src/content/OnBoardingAutoStart'))
);
// Keyword Research
const KeywordResearch = Loader(
  lazy(() => import('src/content/keywordResearch'))
);

// Keyword Overview
const KeywordOverview = Loader(
  lazy(() => import('src/content/keywordResearch/keywordOverview'))
);

// Competitor Analysis
const CompetitorAnalysis = Loader(
  lazy(() => import('src/content/keywordResearch/competitorAnalysis'))
);

// Blog Post Ideas
const BlogPostIdeas = Loader(
  lazy(() => import('src/content/keywordResearch/blogPostIdeas'))
);

// Keywords Lists
const KeywordsLists = Loader(
  lazy(() => import('src/content/keywordResearch/keywordsLists'))
);

const StatusCheck = Loader(
  lazy(() => import('src/content/pages/Status/StatusCheck'))
);

const StatusUpdate = Loader(
  lazy(() => import('src/content/pages/Status/StatusUpdate'))
);

const Dashboard = Loader(lazy(() => import('src/content/dashboard')));

const ScannedPages = Loader(lazy(() => import('src/content/scannedPages')));

const SEOTask = Loader(lazy(() => import('src/content/seotask')));

// Keyword Tracking
const KeywordTracking = Loader(
  lazy(() => import('src/content/keywordResearch/keywordTracking'))
);

// Other Tools
const OtherTools = Loader(lazy(() => import('src/content/OtherTools')));

const MapRankTracker = Loader(lazy(() => import('src/content/mapRankTracker')));

const LocalSeo = Loader(lazy(() => import('src/content/localSeo')));

const GoogleBusinessAuth = Loader(
  lazy(() => import('src/content/GoogleBusiness/Auth'))
);

const TrySeoSpace = Loader(lazy(() => import('src/content/TrySeoSpace')));

const EmbedGoogleReviewsDashboard = Loader(
  lazy(
    () =>
      import(
        'src/content/localSeo/EmbedGoogleReviewsComponents/EmbedGoogleReviewsDashboard'
      )
  )
);

const WidgetsDashboard = Loader(
  lazy(
    () =>
      import(
        'src/content/localSeo/EmbedGoogleReviewsComponents/WidgetsDashboard'
      )
  )
);

const ViewAllSitesDashboard = Loader(
  lazy(
    () =>
      import(
        'src/content/dashboard/ViewAllSitesComponents/ViewAllSitesDashboard'
      )
  )
);

const AiVisibility = Loader(lazy(() => import('src/content/aiVisibility')));

const PdfBuilder = Loader(lazy(() => import('src/content/pdfBuilder')));
const Invitation = Loader(lazy(() => import('src/content/invitation')));

const routes: RouteObject[] = [
  {
    path: '',
    element: <BaseLayout />,
    children: [
      {
        path: '/',
        element: <QueryParamHandler />
      },
      {
        path: 'signin',
        element: <Overview />
      },
      {
        path: 'price',
        element: <PricePlan />
      },
      {
        path: 'plan',
        element: <PlanNew />
      },
      {
        path: 'overview',
        element: <Navigate to="/" replace />
      },
      {
        path: 'signup',
        element: (
          <ProtectedRoutes>
            <GetAffiliateCode>
              <Signup />
            </GetAffiliateCode>
          </ProtectedRoutes>
        )
      },
      {
        path: 'forgot',
        element: <Forgot />
      },
      {
        path: 'reset-password/:id',
        element: <ResetPassword />
      },
      {
        path: 'squarespace-auth',
        element: <SquareSpaceAuthSignUp />
      },
      {
        path: 'squarespace-connect',
        element: <SquareSpaceAuthConnect />
      },
      {
        path: 'status',
        children: [
          {
            path: '',
            element: <Navigate to="success" replace />
          },
          {
            path: 'success',
            element: <MessagePage />
          },
          {
            path: 'fail',
            element: <MessagePage />
          },
          {
            path: '404',
            element: <Status404 />
          },
          {
            path: '500',
            element: <Status500 />
          },
          {
            path: 'check',
            element: <StatusCheck />
          },
          {
            path: 'testing-dev',
            element: <StatusUpdate />
          }
        ]
      },
      {
        path: 'add-on',
        children: [
          {
            path: 'success',
            element: <AddOnStatus />
          }
        ]
      },
      {
        path: '*',
        element: <Status404 />
      },
      {
        path: '/maintenance',
        element: <StatusMaintenance />
      }
    ]
  },
  {
    path: 'external-scans',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: (
          <ProtectedRoutes>
            <IndividualScans />
          </ProtectedRoutes>
        )
      }
    ]
  },
  {
    path: 'site-audit',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: (
          <ProtectedRoutes>
            <DisconnectGSCProvider>
              <SiteAudit />
            </DisconnectGSCProvider>
          </ProtectedRoutes>
        )
      }
    ]
  },

  {
    path: 'settings',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: (
          <ProtectedRoutes>
            <UserSettings />
          </ProtectedRoutes>
        )
      }
    ]
  },
  {
    path: 'billing',
    element: <ProtectedRouteV2><SidebarLayout /></ProtectedRouteV2>,
    children: [
      {
        path: '',
        element: (
          <RequirePermission permission="account.billing">
            <GetMoreScan />
          </RequirePermission>
        )
      }
    ]
  },
  {
    path: 'seo-tasks',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: (
          <ProtectedRoutes>
            {/* <SeoIssues /> */}
            <SEOTask />
          </ProtectedRoutes>
        )
      }
    ]
  },
  {
    path: 'user-management',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: (
          <ProtectedRoutes>
            <UserManagement />
          </ProtectedRoutes>
        )
      }
    ]
  },
  {
    path: 'add-on',
    children: [
      {
        path: 'success',
        element: <AddOnStatus />
      },
      {
        path: 'fail',
        element: <AddOnStatus />
      }
    ]
  },
  {
    path: 'google',
    children: [
      {
        path: '',
        element: (
          <ProtectedRoutes>
            <GoogleAuth />
          </ProtectedRoutes>
        )
      }
    ]
  },
  {
    path: 'member-signup',
    element: (
      <ProtectedRoutes>
        <Member />
      </ProtectedRoutes>
    )
  },
  {
    path: 'lead-generation',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: (
          <ProtectedRoutes>
            <LeadGeneration />
          </ProtectedRoutes>
        )
      }
    ]
  },
  {
    path: 'keyword-research',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: (
          <ProtectedRoutes>
            <KeywordResearch />
          </ProtectedRoutes>
        )
      },
      // Todo: Content Plan - Route for Content Plan dashboard
      /*
      {
        path: 'content-plan',
        element: (
          <ProtectedRoutes>
            <ErrorBoundaryV2>
              <ContentPlanPage />
            </ErrorBoundaryV2>
          </ProtectedRoutes>
        )
      },
      */
      {
        path: 'keyword-explorer',
        element: (
          <ProtectedRoutes>
            <KeywordOverview />
          </ProtectedRoutes>
        )
      },
      {
        path: 'keyword-tracking',
        element: (
          <ProtectedRoutes>
            <KeywordTracking />
          </ProtectedRoutes>
        )
      },
      {
        path: 'competitor-analysis',
        element: (
          <ProtectedRoutes>
            <CompetitorAnalysis />
          </ProtectedRoutes>
        )
      },
      {
        path: 'blog-post-ideas',
        element: (
          <ProtectedRoutes>
            <BlogPostIdeas />
          </ProtectedRoutes>
        )
      },
      {
        path: 'keywords-lists',
        element: (
          <ProtectedRoutes>
            <KeywordsLists />
          </ProtectedRoutes>
        )
      }
    ]
  },
  {
    path: 'leads/:id',
    element: <RenderLeadForm />
  },
  {
    path: 'on-boarding',
    element: (
      <ProtectedRoutes>
        <OnBoarding />
      </ProtectedRoutes>
    )
  },
  {
    path: 'on-boarding-re-audit',
    children: [
      {
        path: '',
        element: (
          <ProtectedRoutes>
            <OnBoardingAutoStart />
          </ProtectedRoutes>
        )
      }
    ]
  },
  {
    path: 'dashboard',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: (
          <ProtectedRoutes>
            <DisconnectGSCProvider>
              <Dashboard />
            </DisconnectGSCProvider>
          </ProtectedRoutes>
        )
      },
      {
        path: 'sites',
        element: (
          <ProtectedRoutes>
            <ViewAllSitesDashboard />
          </ProtectedRoutes>
        )
      }
    ]
  },
  {
    path: 'backlinks',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: (
          <ProtectedRoutes>
            <BackLinkPage />
          </ProtectedRoutes>
        )
      }
    ]
  },
  {
    path: 'scanned-pages',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: (
          <ProtectedRoutes>
            <ScannedPages />
          </ProtectedRoutes>
        )
      }
    ]
  },
  {
    path: 'local-seo',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: (
          <ProtectedRoutes>
            <OtherTools />
          </ProtectedRoutes>
        )
      },
      {
        path: 'business-profile',
        element: (
          <ProtectedRoutes>
            <LocalSeo />
          </ProtectedRoutes>
        ),
        children: [
          {
            path: 'all',
            element: (
              <ProtectedRoutes>
                <ViewAllSitesDashboard isLocalSeo={true} />
              </ProtectedRoutes>
            )
          }
        ]
      },
      {
        path: 'map-rank-tracker',
        element: (
          <ProtectedRoutes>
            <MapRankTracker />
          </ProtectedRoutes>
        )
      },
      {
        path: 'tasks',
        element: (
          <ProtectedRoutes>
            <RecommendActionsCard />
          </ProtectedRoutes>
        )
      },
      {
        path: 'google-reviews',
        element: (
          <ProtectedRoutes>
            <ManageReviewsDashboard />
          </ProtectedRoutes>
        ),
        children: [
          {
            path: 'widgets',
            element: <WidgetsDashboard />
          },
          {
            path: 'embed',
            element: <EmbedGoogleReviewsDashboard />
          }
        ]
      },
      {
        path: 'business-posts',
        element: (
          <ProtectedRoutes>
            <Posting />
          </ProtectedRoutes>
        )
      },
      {
        path: 'listing-manager',
        element: (
          <ProtectedRoutes>
            <ListingManagerDashboard />
          </ProtectedRoutes>
        )
      },
      {
        path: 'citation-waiting-list',
        element: (
          <ProtectedRoutes>
            <ComingSoonCitationBuilder />
          </ProtectedRoutes>
        )
      },
      {
        path: 'citations',
        element: (
          <ProtectedRoutes>
            <RouteDataLoader
              fetchData={async () => {
                const encodedJWT = getCookie(process.env.REACT_APP_COOKIE_NAME);
                const jwtDecode = await decryptEncryptedJWT(encodedJWT);

                return jwtDecode;
              }}
            >
              {(JWTData) => (
                <BusinessProfileAutocompleteProvider>
                  <GoogleBusinessProfileProvider>
                    <CitationProvider>
                      <CitationRoot JWTData={JWTData} />
                    </CitationProvider>
                  </GoogleBusinessProfileProvider>
                </BusinessProfileAutocompleteProvider>
              )}
            </RouteDataLoader>
          </ProtectedRoutes>
        ),
        children: [
          {
            path: '',
            element: <CitationBuilder />
          },
          {
            path: 'list',
            element: <CampaignsList />
          },
          {
            path: 'scanning',
            element: <CitationScanning />
          },
          {
            path: 'create-campaign',
            element: <CreateCampaign />
          },
          {
            path: 'confirm-and-pay',
            element: <ConfirmAndPay />
          },
          {
            path: 'campaign-in-progress',
            element: <CampaignInProgress />
          },
          {
            path: 'report',
            element: <CitationReport />
          }
        ]
      }
    ]
  },
  {
    path: 'google-business',
    children: [
      {
        path: '',
        element: (
          <ProtectedRoutes>
            <GoogleBusinessAuth />
          </ProtectedRoutes>
        )
      }
    ]
  },
  {
    path: 'google-review-widget/:id',
    element: <WidgetPreviewComponent isScript={true} />
  },
  {
    path: 'try-seospace',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: (
          <ProtectedRoutes>
            <TrySeoSpace />
          </ProtectedRoutes>
        )
      }
    ]
  },
  {
    path: 'ai-visibility',
    element: <ProtectedRouteV2><SidebarLayout /></ProtectedRouteV2>,
    children: [
      {
        path: '',
        element: (<AiVisibility />)
      }
    ]
  },
  {
    path: '/reports/pdf/:id',
    element: (
      <ProtectedRoutes>
        <GoogleBusinessProfileProvider>
          <PdfBuilder />
        </GoogleBusinessProfileProvider>
      </ProtectedRoutes>
    )
  },
  {
    path: '/seo-report/:reportId',
    element: (
      <>
        <PDFBuilderPublicRoute />
      </>
    )
  },
  {
    path: 'invitation',
    element: (
      <ProtectedRoutes>
        <Invitation />
      </ProtectedRoutes>
    )
  },
  {
    path: 'orbit-ai-agent',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: (
          <ProtectedRoutes>
            <OrbitAiDashboard />
          </ProtectedRoutes>
        )
      },
      {
        path: 'keyword-assist',
        element: (
          <ProtectedRoutes>
            <ErrorBoundaryV2>
              <KeywordAssistPage />
            </ErrorBoundaryV2>
          </ProtectedRoutes>
        )
      },
      {
        path: 'content-strategy',
        element: (
          <ProtectedRoutes>
            <ErrorBoundaryV2>
              <ContentStrategyPage />
            </ErrorBoundaryV2>
          </ProtectedRoutes>
        )
      },
      // Todo: Content Plan - Route for Content Plan dashboard
      /*
      {
        path: 'content-plan',
        element: (
          <ProtectedRoutes>
            <ErrorBoundaryV2>
              <ContentPlanPage />
            </ErrorBoundaryV2>
          </ProtectedRoutes>
        )
      },
      */
      {
        path: 'blog-post-agent',
        element: (
          <ProtectedRoutes>
            <ErrorBoundaryV2>
              <BlogGenerationPage />
            </ErrorBoundaryV2>
          </ProtectedRoutes>
        )
      },
      {
        path: 'local-seo-ai',
        element: (
          <ProtectedRoutes>
            <ProtectedRouteV2>
              <ErrorBoundaryV2>
                <LocalSeoAiPage />
              </ErrorBoundaryV2>
            </ProtectedRouteV2>
          </ProtectedRoutes>
        )
      },
      {
        path: 'review-automation',
        element: (
          <ProtectedRoutes>
            <ReviewAutomationPage />
          </ProtectedRoutes>
        )
      }
    ]
  },
  {
    path: 'orbit-ai-addon',
    element: <OrbitAIPublicLinkHandler />
  },
  {
    path: 'orbit-ai',
    element: <OrbitAIPublicLinkHandler />
  }
];

export default routes;
